import { useEffect, useState, type ReactNode } from 'react'
import { faSeedling } from '@fortawesome/pro-solid-svg-icons'
import { Button, Icon, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { TransactionCategoryDropdown } from '@/components/TransactionCategoryDropdown'
import { useMerchantCategorization } from '../../hooks/useMerchantCategorization'
import { plural } from '../../lib/plural'
import { MerchantLogo } from '@/components/MerchantLogo'
import { BankDetailLines } from './BankDetailLines'
import { BUTTON_ROW, Shell } from './cardParts'
import type { MerchantReview } from '../../merchantReview.slice'
import type { WidgetProps } from './types'

const Header = ({
  group,
  progress,
  accounts,
}: {
  group: MerchantReview
  progress: { position: number; total: number } | null
  accounts: ReactNode
}) => (
  <div className="flex items-center gap-3">
    <MerchantLogo
      key={group.groupKey}
      name={group.merchant}
      logoUrl={group.logoUrl}
    />

    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {progress && (
        <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
          {progress.position} of {progress.total}
        </Text>
      )}
      <Text variant={TypographyStyles.LABEL_MD} color={TextColors.HEADER}>
        {group.merchant}
      </Text>
      <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
        {plural(group.transactionCount, 'transaction')} · {accounts}
      </Text>
    </div>

    <Text
      variant={TypographyStyles.LABEL_MD}
      color={TextColors.HEADER}
      className="shrink-0 tabular-nums"
    >
      {group.total}
    </Text>
  </div>
)

const Evidence = ({
  verdict,
  reason,
}: {
  verdict: string
  reason: string | null
}) => (
  <div className="flex gap-2 rounded-[8px] bg-moss-0 p-3">
    <Icon
      icon={faSeedling}
      color="text-moss-200"
      className="mt-0.5 shrink-0 text-[12px]"
    />
    <div className="flex min-w-0 flex-col gap-1">
      <Text variant={TypographyStyles.LABEL_XS} color={TextColors.MOSS}>
        {verdict}
      </Text>
      {reason && (
        <Text
          variant={TypographyStyles.BODY_XS}
          className="line-clamp-3 text-neutral-600"
        >
          {reason}
        </Text>
      )}
    </div>
  </div>
)

const toGroupKeys = (input: Record<string, unknown>): string[] => {
  if (Array.isArray(input.groupKeys)) {
    return input.groupKeys.filter(
      (key): key is string => typeof key === 'string'
    )
  }
  if (typeof input.groupKey === 'string') return [input.groupKey]
  return []
}

const NO_SUGGESTION_REASON =
  'Heard has nothing to go on for this merchant, so pick the category yourself.'

export const CategorizeTransactionsWidget = ({
  input,
  resolved,
  onOutcome,
}: WidgetProps) => {
  const groupKeys = toGroupKeys(input)
  const {
    group,
    progress,
    groupCategoryId,
    primaryLabel,
    nameOf,
    categoryOf,
    setGroupCategory,
    setRowCategory,
    isRowPersonal,
    toggleRowPersonal,
    canSave,
    submit,
    markPersonal,
    askForHelp,
    saving,
  } = useMerchantCategorization(groupKeys, onOutcome)
  const [expanded, setExpanded] = useState(false)
  const [accountsShown, setAccountsShown] = useState(false)

  const merchantKey = group?.groupKey
  useEffect(() => {
    setExpanded(false)
    setAccountsShown(false)
  }, [merchantKey])

  if (!group || resolved) return null

  const [firstAccount, ...otherAccounts] = group.accounts
  const suggested = group.suggestedCategoryIds[0]

  const accounts = (
    <>
      {accountsShown ? group.accounts.join(', ') : firstAccount}
      {otherAccounts.length > 0 && (
        <>
          {accountsShown ? ' · ' : ' and '}
          <Button
            variant="link"
            className="h-auto p-0 align-baseline text-xs"
            onClick={() => setAccountsShown(!accountsShown)}
          >
            {accountsShown ? 'Show less' : `${otherAccounts.length} more`}
          </Button>
        </>
      )}
    </>
  )

  return (
    <Shell>
      <Header group={group} progress={progress} accounts={accounts} />

      <div className="flex flex-col gap-1">
        <Text variant={TypographyStyles.LABEL_XS} color={TextColors.HEADER}>
          Category
        </Text>
        <TransactionCategoryDropdown
          optionStyle="normal"
          className="body-sm"
          buttonWidthClassName="w-full min-w-0"
          value={groupCategoryId ?? undefined}
          disabled={saving}
          transactionAmount={group.totalInCents}
          transactionDate={group.transactions[0]?.isoDate}
          onChange={setGroupCategory}
        />
      </div>

      <Button
        variant="link"
        className="h-auto justify-start p-0 text-xs"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded
          ? 'Hide transactions'
          : `View all ${plural(group.transactionCount, 'transaction')}`}
      </Button>

      {expanded && (
        <ul>
          {group.transactions.map((transaction) => (
            <li
              key={transaction.id}
              className="flex flex-col gap-2 border-t border-neutral-50 py-2 first:border-t-0 sm:flex-row sm:items-center sm:gap-3"
            >
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <Text
                  variant={TypographyStyles.BODY_XS}
                  color={TextColors.MUTED}
                  className="w-[5.5rem] shrink-0 whitespace-nowrap pt-0.5"
                >
                  {transaction.date}
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  className="min-w-0 flex-1 [overflow-wrap:anywhere]"
                >
                  <BankDetailLines
                    details={transaction.details}
                    description={transaction.description}
                  />
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  textAlign="right"
                  className="w-24 shrink-0 whitespace-nowrap tabular-nums"
                >
                  {transaction.amount}
                </Text>
              </div>
              <div className="flex w-full items-center gap-2 sm:w-[310px] sm:shrink-0">
                <div className="min-w-0 flex-1">
                  {isRowPersonal(transaction.id) ? (
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      color={TextColors.MUTED}
                    >
                      Personal, no category
                    </Text>
                  ) : (
                    <TransactionCategoryDropdown
                      optionStyle="normal"
                      className="body-sm"
                      buttonWidthClassName="w-full min-w-0"
                      widthClassName="min-w-[220px]"
                      clearable={false}
                      value={categoryOf(transaction) ?? undefined}
                      disabled={saving}
                      transactionAmount={transaction.amountInCents}
                      transactionDate={transaction.isoDate}
                      onChange={(value) =>
                        setRowCategory(transaction.id, value)
                      }
                    />
                  )}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className={mtc(
                    'shrink-0',
                    isRowPersonal(transaction.id) &&
                      'border-neutral-700 font-medium text-neutral-900'
                  )}
                  aria-pressed={isRowPersonal(transaction.id)}
                  aria-label={`Mark ${transaction.description} personal`}
                  disabled={saving}
                  onClick={() => toggleRowPersonal(transaction.id)}
                >
                  Personal
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Evidence
        verdict={
          suggested === undefined
            ? 'No best fit yet'
            : `Best fit is ${nameOf(suggested)}`
        }
        reason={
          suggested === undefined
            ? NO_SUGGESTION_REASON
            : group.suggestionReasoning
        }
      />

      <div className={BUTTON_ROW}>
        <Button
          variant="primary"
          size="sm"
          className="max-w-full"
          title={primaryLabel}
          disabled={!canSave}
          loading={saving}
          onClick={submit}
        >
          {/* A span rather than Text, which renders a <p> and is invalid inside a button. */}
          <span className="min-w-0 truncate">{primaryLabel}</span>
        </Button>
        {!expanded && (
          <Button
            variant="secondary"
            size="sm"
            className="shrink-0"
            disabled={saving}
            onClick={() => setExpanded(true)}
          >
            It&apos;s a mix
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          disabled={saving}
          onClick={markPersonal}
        >
          Personal
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          disabled={saving}
          onClick={askForHelp}
        >
          I&apos;m not sure
        </Button>
      </div>
    </Shell>
  )
}
